import {
  ReceiptHeaderText,
  ReceiptFooterText,
  CustomerAccountBlock,
  type ReceiptExtrasSettings,
} from './ReceiptExtras'

// THE single receipt/invoice template. Used by POS (thermal receipt), Sale Detail / New Sale
// (invoice print), and the Printer Settings live preview — so what you preview is what prints,
// everywhere. Composed from the shared ReceiptExtras (custom header/footer + customer account
// block). Render-only: it reads nothing and writes nothing; every value is passed in.
//
// Modes:
//   • print  (default): wrapper is `.print-invoice hidden print:block` — invisible on screen,
//     and the `.print-invoice` isolation rule in index.css prints ONLY this.
//   • preview (preview): wrapper is a plain on-screen block (no `.print-invoice`, not hidden),
//     for the settings preview pane.
// Width is driven by the tenant's paper-width setting (58/80mm), to-scale on screen and in print.

export type InvoiceReceiptItem = {
  name: string
  qty: number
  unitPrice: number
  lineTotal: number
  // Optional bundle breakdown (POS bundles print their components underneath).
  components?: Array<{ name: string; qty: number }>
}

export type InvoiceReceiptProps = {
  business: { name: string; address?: string | null; mobile?: string | null }
  meta: { docTitle?: string; number: string; dateTime: Date; servedBy?: string | null; register?: string | null }
  customer?: { name: string; phone?: string | null; address?: string | null } | null
  items: InvoiceReceiptItem[]
  totals: {
    subtotal: number
    discount?: number
    discountLabel?: string
    vat?: number
    shipping?: number
    total: number
    amountPaid: number
    change?: number
  }
  paymentMethod?: string | null
  // Customer running-balance block. Shown only when show is true, a customer is present, and
  // nowOwing has loaded. thisSale/paidNow come from totals so it foots: prev + this − paid = owing.
  account?: { show: boolean; nowOwing: number | null }
  extras: ReceiptExtrasSettings
  format: (n: number) => string
  paperWidth?: '58' | '80'
  preview?: boolean
}

// Rows wrap instead of clipping: a long label pushes its value to the next line
// rather than off the paper. Numeric values carry their own nowrap so they never
// break mid-number.
const row: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: '0 8px', flexWrap: 'wrap', marginBottom: 2 }
const val: React.CSSProperties = { whiteSpace: 'nowrap', marginLeft: 'auto' }
const dash: React.CSSProperties = { borderTop: '1px dashed #000', margin: '4px 0' }

export function InvoiceReceipt({
  business, meta, customer, items, totals, paymentMethod, account, extras, format,
  paperWidth = '80', preview = false,
}: InvoiceReceiptProps) {
  const balanceOnSale = totals.total - totals.amountPaid
  const showAccount = !!account?.show && !!customer && account.nowOwing != null
  const width = paperWidth === '58' ? '58mm' : '80mm'
  // One readable step up from the old flat 11/15/13. Scaled consistently (name = base+3,
  // TOTAL = base+2) and driven by paper width: 58mm takes a slightly smaller step than 80mm
  // so the narrower paper still fits without clipping (wrapping guards below stay intact).
  const fs = paperWidth === '58'
    ? { base: 12, name: 15, total: 14 }
    : { base: 13, name: 16, total: 15 }
  const outerClass = preview ? 'bg-white' : 'print-invoice hidden print:block bg-white'

  // Compartmentalised meta: a fixed label column keeps every value left-aligned in a
  // straight column, so Receipt / Date / Customer / Phone / Address read as an aligned
  // block. Label stays whole (nowrap); the value wraps (never clips) on a long address.
  const metaRow = (label: string, value: React.ReactNode) => (
    <div style={{ display: 'flex', gap: '0 6px', marginBottom: 1 }}>
      <span style={{ width: 80, flexShrink: 0, whiteSpace: 'nowrap' }}>{label}</span>
      <span style={{ flex: 1, minWidth: 0, overflowWrap: 'break-word', wordBreak: 'break-word' }}>{value}</span>
    </div>
  )

  return (
    <div className={outerClass} style={{ color: '#000' }}>
      {/* Print at the EXACT paper width so the thermal driver doesn't scale/clip an A4
          page. Only when actually printing (not the on-screen settings preview). */}
      {!preview && (
        <style dangerouslySetInnerHTML={{ __html: `@media print { @page { size: ${width} auto; margin: 0; } }` }} />
      )}
      <div style={{
        width, maxWidth: width, boxSizing: 'border-box', margin: '0 auto',
        fontFamily: 'monospace', fontSize: `${fs.base}px`, padding: '4px 6px 10px',
        // Everything wraps to the paper: long words / account numbers / free text break
        // rather than run off the right edge. (break-word leaves normal numbers intact.)
        overflowWrap: 'break-word', wordBreak: 'break-word',
      }}>
        {/* Header: custom header text (verbatim) then business identity */}
        <ReceiptHeaderText text={extras.headerText} />
        <p style={{ textAlign: 'center', fontWeight: 'bold', fontSize: `${fs.name}px`, marginBottom: '2px', overflowWrap: 'break-word' }}>{business.name}</p>
        {business.address && business.address.trim() && (
          <p style={{ textAlign: 'center', whiteSpace: 'pre-line', marginBottom: '2px' }}>{business.address}</p>
        )}
        {business.mobile && business.mobile.trim() && (
          <p style={{ textAlign: 'center', marginBottom: '2px' }}>{business.mobile}</p>
        )}

        <div style={dash} />

        {/* Meta compartment — doc title heading then an aligned label/value block. */}
        <div style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 3 }}>{meta.docTitle ?? 'INVOICE'}</div>
        {metaRow('Invoice No', meta.number)}
        {metaRow('Date',
          `${meta.dateTime.toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })} ${meta.dateTime.toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' })}`,
        )}
        {meta.servedBy && metaRow('Cashier', meta.servedBy)}
        {meta.register && metaRow('Register', meta.register)}
        {/* For any sale WITH a customer, Name / Phone / Address always print — an empty
            field shows "-" (not hidden) so staff notice it's missing on the record and
            fix it. A true walk-in (no customer) omits the whole block. */}
        {customer && metaRow('Customer', customer.name && customer.name.trim() ? customer.name : '-')}
        {customer && metaRow('Phone', customer.phone && customer.phone.trim() ? customer.phone : '-')}
        {customer && metaRow('Address', customer.address && customer.address.trim() ? <span style={{ whiteSpace: 'pre-line' }}>{customer.address}</span> : '-')}

        <div style={dash} />

        {/* Purchases — an Excel-style table: a fixed SN column and a Product column. The
            product NAME is the column head of each row; its qty×price and line total sit
            underneath, inside the Product column, so a wrapped name hangs within the column
            (SN stays put). Amount (line total) is right-aligned + nowrap — never clips. */}
        <div style={{ fontWeight: 'bold', textDecoration: 'underline', marginBottom: 3 }}>Purchases</div>
        <div style={{ display: 'flex', gap: '0 6px', fontWeight: 'bold', borderBottom: '1px solid #000', paddingBottom: 1, marginBottom: 2 }}>
          <span style={{ width: '2em', flexShrink: 0 }}>SN</span>
          <span style={{ flex: 1, minWidth: 0 }}>Product</span>
          <span style={{ whiteSpace: 'nowrap' }}>Amount</span>
        </div>
        {items.map((it, i) => (
          <div key={i} style={{ display: 'flex', gap: '0 6px', marginBottom: 3 }}>
            <span style={{ width: '2em', flexShrink: 0, fontWeight: 'bold' }}>{i + 1}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 'bold', overflowWrap: 'break-word' }}>{it.name}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 8px' }}>
                <span style={{ whiteSpace: 'nowrap' }}>{it.qty} × {format(it.unitPrice)}</span>
                <span style={{ whiteSpace: 'nowrap', marginLeft: 'auto' }}>{format(it.lineTotal)}</span>
              </div>
              {it.components && it.components.map((c, ci) => (
                <div key={ci} style={{ color: '#444' }}>- {c.name} ×{c.qty}</div>
              ))}
            </div>
          </div>
        ))}

        <div style={dash} />

        {/* Totals — money values use `val` (nowrap, pushed right) so a long label
            wraps and the figure stays whole and aligned, never clipped. */}
        <div style={{ ...row, fontWeight: 'bold' }}>
          <span>Total Items</span><span style={val}>{items.reduce((s, it) => s + it.qty, 0)}</span>
        </div>
        {((totals.discount ?? 0) > 0.001 || (totals.vat ?? 0) > 0.001) && (
          <div style={row}><span>Subtotal</span><span style={val}>{format(totals.subtotal)}</span></div>
        )}
        {(totals.discount ?? 0) > 0.001 && (
          <div style={row}><span>{totals.discountLabel ?? 'Discount'}</span><span style={val}>-{format(totals.discount!)}</span></div>
        )}
        {(totals.vat ?? 0) > 0.001 && (
          <div style={row}><span>VAT</span><span style={val}>{format(totals.vat!)}</span></div>
        )}
        {(totals.shipping ?? 0) > 0.001 && (
          <div style={row}><span>Shipping</span><span style={val}>{format(totals.shipping!)}</span></div>
        )}
        <div style={{ ...row, fontWeight: 'bold', fontSize: `${fs.total}px`, marginTop: 2 }}>
          <span>TOTAL</span><span style={val}>{format(totals.total)}</span>
        </div>
        <div style={row}><span>Amount paid</span><span style={val}>{format(totals.amountPaid)}</span></div>
        {balanceOnSale > 0.001 && (
          <div style={row}><span>Balance on this sale</span><span style={val}>{format(balanceOnSale)}</span></div>
        )}
        {paymentMethod && <div style={{ marginTop: 2 }}>Payment: {paymentMethod}</div>}
        {(totals.change ?? 0) > 0.001 && <div>Change: {format(totals.change!)}</div>}

        {/* Customer running balance ("now owing") — real customer + toggle on + loaded */}
        {showAccount && (
          <CustomerAccountBlock
            thisSale={totals.total}
            paidNow={totals.amountPaid}
            nowOwing={account!.nowOwing!}
            format={format}
          />
        )}

        <div style={{ ...dash, margin: '6px 0' }} />
        <ReceiptFooterText text={extras.footerText} />
      </div>
    </div>
  )
}
